import ReactMarkdown from "react-markdown"

import remarkGfm from "remark-gfm"

import { FileBadge } from "@/components/badges/FileBadge"

interface MarkdownDisplayProps {
    content: string
}
export function MarkdownDisplay({ content }: Readonly<MarkdownDisplayProps>) {
    const convertedContent = convertLinks(content) as string

    return (
        <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            components={{
                a: ({ node, ...props }: { node?: any; href?: string; children?: React.ReactNode }) => {
                    const href = props.href || ""

                    if (href.startsWith("/store/objects/")) {
                        const documentId = href.split("/store/objects/")[1]
                        return (
                            <FileBadge id={documentId} name={String(props?.children || "")} />
                        )
                    }
                    return (
                        <a
                            {...props}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800"
                        />
                    )
                },
                p: ({ node, ...props }: { node?: any; children?: React.ReactNode }) => (
                    <p {...props} className={`my-0`} />
                ),
                pre: ({ node, ...props }: { node?: any; children?: React.ReactNode }) => (
                    <pre {...props} className={`my-2 p-2 rounded`} />
                ),
                code: ({
                    node,
                    className,
                    children,
                    ...props
                }: {
                    node?: any;
                    className?: string;
                    children?: React.ReactNode;
                }) => {
                    const match = /language-(\w+)/.exec(className || "")
                    const isInline = !match
                    const language = match ? match[1] : ""
                    return (
                        <>
                            {!isInline && language && (
                                <div className="code-language-indicator bg-gray-700 text-gray-200 text-xs px-3 py-1 rounded-t-md font-mono border-t border-l border-r border-gray-700 -mb-2">
                                    {language}
                                </div>
                            )}
                            <code
                                {...props}
                                className={
                                    isInline
                                        ? "px-1.5 py-0.5 rounded text-gray-700 bg-gray-200/70 font-mono text-sm"
                                        : "text-gray-100 font-mono text-sm"
                                }
                            >
                                {children}
                            </code>
                        </>
                    )
                },
                h1: ({ node, ...props }: { node?: any; children?: React.ReactNode }) => (
                    <h1 {...props} className={`font-bold text-2xl my-2`} />
                ),
                h2: ({ node, ...props }: { node?: any; children?: React.ReactNode }) => (
                    <h2 {...props} className={`font-bold text-xl my-2`} />
                ),
                h3: ({ node, ...props }: { node?: any; children?: React.ReactNode }) => (
                    <h3 {...props} className={`font-bold text-lg my-2`} />
                ),li: ({ node, ...props }: { node?: any; children?: React.ReactNode }) => (
                    <li {...props} className="my-1" />
                ),
                ul: ({ node, ...props }: { node?: any; children?: React.ReactNode }) => (
                    <ul {...props} className="list-disc ml-6 my-2" />
                ),
                ol: ({ node, ...props }: { node?: any; children?: React.ReactNode }) => (
                    <ol {...props} className="list-decimal ml-6 my-2" />
                ),
                table: ({ node, ...props }: { node?: any; children?: React.ReactNode }) => (
                    <div className="overflow-x-auto ">
                        <table
                            {...props}
                            className="min-w-full border-collapse border border-gray-300"
                        />
                    </div>
                ),
                th: ({ node, ...props }: { node?: any; children?: React.ReactNode }) => (
                    <th
                        {...props}
                        className="border border-gray-300 bg-gray-100 px-4 py-2 text-left text-gray-700"
                    />
                ),
                td: ({ node, ...props }: { node?: any; children?: React.ReactNode }) => (
                    <td
                        {...props}
                        className="border border-gray-300 px-4 py-2 text-gray-800"
                    />
                ),
            }}
        >
            {convertedContent}
        </ReactMarkdown>
    )
}

function convertLinks(content: string | object) {
    if (typeof content !== "string") {
        return content
    }

    return content.replace(/\[([^\]]+)\]\(store:([a-f\d]{24})\)/gi, (_, text, id) => {
        return `[${text}](/store/objects/${id})`
    })
}